import {
  DataGridBox,
  DeleteDialog,
  CustomToolbar,
  ZebraDataGrid,
  CrudActionButton,
  PermissionHandler,
  CustomNoRowsOverlay,
} from 'components';
import apiService from 'library/api-service';
import { Grid2 as Grid } from '@mui/material';
import { renderDataGridLocaleText } from 'utils';
import {
  selectSalesStageTypes,
  selectSalesStageTypesIsEmpty,
  selectSalesStageTypesLoading,
} from 'store/features/sales-stage-types/selector';
import { useSingleRowSelectionGuard } from 'hooks';
import { useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { GridColDef, GridRowSelectionModel } from '@mui/x-data-grid-premium';
import salesStageTypesActions from 'store/features/sales-stage-types/actions';
import CreateSalesStageTypeDialog from './components/create-sales-stage-type-dialog';
import UpdateSalesStageTypeDialog from './components/update-sales-stage-type-dialog';

const SalesStageTypes = () => {
  // other hooks
  const intl = useIntl();
  const requireSingleRowSelection = useSingleRowSelectionGuard();

  // redux hooks
  const dispatch = useAppDispatch();
  const loading = useAppSelector(selectSalesStageTypesLoading);
  const salesStageTypes = useAppSelector(selectSalesStageTypes);
  const isEmptySalesStageTypes = useAppSelector(selectSalesStageTypesIsEmpty);

  // useState hooks
  const [openDeleteSalesStageType, setOpenDeleteSalesStageType] = useState(false);
  const [openCreateSalesStageType, setOpenCreateSalesStageType] = useState(false);
  const [openUpdateSalesStageType, setOpenUpdateSalesStageType] = useState(false);
  const [rowSelectionModel, setRowSelectionModel] = useState<GridRowSelectionModel>([]);

  // dynamic variables
  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'title',
        headerName: intl.formatMessage({ id: 'salesStageTypes.title', defaultMessage: 'Adı' }),
        flex: 1,
      },
    ],
    [intl]
  );

  // async helper functions
  const refresh = async () => {
    dispatch(salesStageTypesActions.getSalesStageTypes());
  };

  // sync helper functions
  const handleCloseDeleteSalesStageType = () => setOpenDeleteSalesStageType(false);
  const handleClickOpenDeleteSalesStageType = () => {
    if (requireSingleRowSelection(rowSelectionModel) === null) return;

    // If the check has been passed successfully...
    setOpenDeleteSalesStageType(true);
  };

  const handleCloseCreateSalesStageType = () => setOpenCreateSalesStageType(false);
  const handleClickOpenCreateSalesStageType = () => setOpenCreateSalesStageType(true);

  const handleCloseUpdateSalesStageType = () => setOpenUpdateSalesStageType(false);
  const handleClickOpenUpdateSalesStageType = () => {
    if (requireSingleRowSelection(rowSelectionModel) === null) return;

    // If the check has been passed successfully...
    setOpenUpdateSalesStageType(true);
  };

  // useEffect hooks
  useEffect(() => {
    if (!salesStageTypes.length && !isEmptySalesStageTypes) {
      dispatch(salesStageTypesActions.getSalesStageTypes());
    }
  }, [salesStageTypes, isEmptySalesStageTypes, dispatch]);

  return (
    <>
      <Grid container justifyContent="flex-start" spacing={1}>
        <PermissionHandler requiredPermissionCodes={['add-sales-stage-type']}>
          <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
            <CrudActionButton action="create" onClick={handleClickOpenCreateSalesStageType}>
              <FormattedMessage id="salesStageTypes.createSalesStageType" defaultMessage="Satış Aşaması Oluştur" />
            </CrudActionButton>
          </Grid>
        </PermissionHandler>
        <PermissionHandler requiredPermissionCodes={['update-sales-stage-type']}>
          <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
            <CrudActionButton action="update" onClick={handleClickOpenUpdateSalesStageType}>
              <FormattedMessage id="salesStageTypes.updateSalesStageType" defaultMessage="Satış Aşaması Düzenle" />
            </CrudActionButton>
          </Grid>
        </PermissionHandler>
        <PermissionHandler requiredPermissionCodes={['delete-sales-stage-type']}>
          <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
            <CrudActionButton action="delete" onClick={handleClickOpenDeleteSalesStageType}>
              <FormattedMessage id="salesStageTypes.deleteSalesStageType" defaultMessage="Satış Aşaması Sil" />
            </CrudActionButton>
          </Grid>
        </PermissionHandler>
      </Grid>
      <CreateSalesStageTypeDialog
        open={openCreateSalesStageType}
        handleClose={handleCloseCreateSalesStageType}
        refresh={refresh}
      />
      <UpdateSalesStageTypeDialog
        open={openUpdateSalesStageType}
        salesStageTypeId={rowSelectionModel[0]}
        handleClose={handleCloseUpdateSalesStageType}
        refresh={refresh}
      />
      <DeleteDialog
        open={openDeleteSalesStageType}
        id={rowSelectionModel[0]}
        handleClose={handleCloseDeleteSalesStageType}
        refresh={refresh}
        setRowSelectionModel={setRowSelectionModel}
        title={<FormattedMessage id="salesStageTypes.deleteSalesStageType" defaultMessage="Satış Aşaması Sil" />}
        subTitle={
          <FormattedMessage
            id="salesStageTypes.deleteSalesStageTypeRepeat"
            defaultMessage="Bu işlem onaylandıktan sonra satış aşaması veritabanından silinecektir. Silme işlemini onaylıyor musunuz?"
          />
        }
        action={apiService.deleteSalesStageType}
        successMessage={
          <FormattedMessage
            id="salesStageTypes.deleteSalesStageTypeCompleted"
            defaultMessage="Seçili satış aşamasını silme işlemi başarıyla tamamlandı."
          />
        }
      />
      <br />
      <DataGridBox>
        <ZebraDataGrid
          /* Pagination Settings */
          pagination
          /* Chekbox Settings */
          checkboxSelection
          onRowSelectionModelChange={(newRowSelectionModel) => {
            setRowSelectionModel(newRowSelectionModel);
          }}
          rowSelectionModel={rowSelectionModel}
          /* Remain Settings */
          columns={columns}
          disableRowSelectionOnClick
          getRowId={(row) => row.id}
          loading={loading}
          localeText={renderDataGridLocaleText(intl)}
          rows={salesStageTypes}
          slots={{
            noRowsOverlay: CustomNoRowsOverlay,
            toolbar: () => CustomToolbar({ exportPremiumExcel: true }),
          }}
          disableAggregation
          disableRowGrouping
        />
      </DataGridBox>
    </>
  );
};

export default SalesStageTypes;
